import { Injectable, OnDestroy } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  interval,
  map,
  Observable,
  Subscription,
  switchMap,
} from 'rxjs';
import { ProductActionType } from '../../shared/enums/product-action-type.enum';
import { BarcodesApi } from '../api/barcodes/barcodes-api.service';
import { ProductsApi } from '../api/products/products-api.service';
import { StockItemsApi } from '../api/stock-items/stock-items-api.service';
import { UserPreferencesApi } from '../api/user-preferences/user-preferences-api.service';
import { ScannerMode, ScannerScanResult, ScannerState } from '../models/scanner-state.model';
import { AudioFeedbackService } from './audio-feedback.service';
import { BarcodeListenerService } from './barcode-listener.service';

const DEFAULT_BARCODES = {
  consume: 'SKYE_CONSUME',
  open: 'SKYE_OPEN',
  info: 'SKYE_INFO',
  close: 'SKYE_CLOSE',
  undo: 'SKYE_UNDO',
  consumeOpen: 'SKYE_CONSUME_OPEN',
} as const;

const IDLE_TIMEOUT_SECONDS = 60;

type ActiveState = Extract<ScannerState, { status: 'active' }>;

@Injectable({ providedIn: 'root' })
export class ScannerService implements OnDestroy {
  private state$ = new BehaviorSubject<ScannerState>({ status: 'idle' });
  private timerSub?: Subscription;
  private barcodeSub: Subscription;
  private barcodeConfigSub: Subscription;
  private lastActivityTime = 0;
  private barcodeConfig: Record<keyof typeof DEFAULT_BARCODES, string> = { ...DEFAULT_BARCODES };

  constructor(
    private audioFeedback: AudioFeedbackService,
    private barcodeListener: BarcodeListenerService,
    private oidcSecurityService: OidcSecurityService,
    private userPreferencesApi: UserPreferencesApi,
    private barcodesApi: BarcodesApi,
    private productsApi: ProductsApi,
    private stockItemsApi: StockItemsApi
  ) {
    this.barcodeConfigSub = this.loadBarcodeConfigWhenAuthenticated();
    this.barcodeSub = this.barcodeListener.barcodes$.subscribe(b => this.handleBarcode(b));
  }

  get state(): Observable<ScannerState> {
    return this.state$.asObservable();
  }

  ngOnDestroy(): void {
    this.barcodeSub.unsubscribe();
    this.barcodeConfigSub.unsubscribe();
    this.timerSub?.unsubscribe();
  }

  // This service is created at bootstrap by the scanner panel, outside the router's auth guards,
  // so it has to wait for authentication itself (including the token exchange on /callback)
  private loadBarcodeConfigWhenAuthenticated(): Subscription {
    return this.oidcSecurityService.isAuthenticated$
      .pipe(
        map(({ isAuthenticated }) => isAuthenticated),
        distinctUntilChanged(),
        filter(isAuthenticated => isAuthenticated),
        // Keep the default barcodes if loading fails; ApiService already shows the error
        switchMap(() => this.userPreferencesApi.get().pipe(catchError(() => EMPTY)))
      )
      .subscribe(prefs => {
        this.barcodeConfig = {
          consume: prefs.scannerConsumeBarcode ?? DEFAULT_BARCODES.consume,
          open: prefs.scannerOpenBarcode ?? DEFAULT_BARCODES.open,
          info: prefs.scannerInfoBarcode ?? DEFAULT_BARCODES.info,
          close: prefs.scannerCloseBarcode ?? DEFAULT_BARCODES.close,
          undo: prefs.scannerUndoBarcode ?? DEFAULT_BARCODES.undo,
          consumeOpen: prefs.scannerConsumeOpenBarcode ?? DEFAULT_BARCODES.consumeOpen,
        };
      });
  }

  private modeForBarcode(barcode: string): ScannerMode | null {
    if (barcode === this.barcodeConfig.consume) return 'consume';
    if (barcode === this.barcodeConfig.open) return 'open';
    if (barcode === this.barcodeConfig.info) return 'info';
    if (barcode === this.barcodeConfig.consumeOpen) return 'consume-open';
    return null;
  }

  private handleBarcode(barcode: string): void {
    const state = this.state$.value;

    if (barcode === this.barcodeConfig.close) {
      this.deactivate();
      return;
    }

    const mode = this.modeForBarcode(barcode);

    if (state.status === 'idle') {
      if (mode) this.activate(mode);
      return;
    }

    if (state.status === 'active') {
      if (mode) {
        this.stopTimer();
        this.activate(mode);
        return;
      }
      if (barcode === this.barcodeConfig.undo) {
        this.handleUndo(state);
        return;
      }
      this.handleProductScan(barcode, state);
    }
  }

  private activate(mode: ScannerMode): void {
    this.lastActivityTime = Date.now();
    this.state$.next({
      status: 'active',
      mode,
      secondsRemaining: IDLE_TIMEOUT_SECONDS,
      lastScan: null,
      recentScans: [],
    });
    this.audioFeedback.modeActivated();
    this.startTimer();
  }

  private deactivate(): void {
    this.stopTimer();
    this.state$.next({ status: 'idle' });
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerSub = interval(1000).subscribe(() => this.tick());
  }

  private stopTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = undefined;
  }

  private tick(): void {
    const state = this.state$.value;
    if (state.status !== 'active') return;
    const elapsed = Math.floor((Date.now() - this.lastActivityTime) / 1000);
    const remaining = IDLE_TIMEOUT_SECONDS - elapsed;
    if (remaining <= 0) {
      this.deactivate();
    } else {
      this.state$.next({ ...state, secondsRemaining: remaining });
    }
  }

  private resetActivity(): void {
    this.lastActivityTime = Date.now();
  }

  private handleUndo(state: ActiveState): void {
    const last = state.lastScan;
    if (!last) return;
    if (last.kind !== 'action' && last.kind !== 'consume-open') return;
    if (last.undone) return;

    const stockItemId = last.kind === 'action' ? last.stockItemId : last.consumeStockItemId;
    const actionType = last.kind === 'action' ? last.actionType : ProductActionType.Consume;

    this.resetActivity();
    this.stockItemsApi.undoRecentAction(stockItemId, actionType).subscribe(() => {
      const currentState = this.state$.value;
      if (currentState.status !== 'active') return;
      const updatedLast: ScannerScanResult = { ...last, undone: true };
      this.state$.next({
        ...currentState,
        lastScan: updatedLast,
        recentScans: currentState.recentScans.map(s => (s === last ? updatedLast : s)),
      });
    });
  }

  private handleProductScan(barcode: string, state: ActiveState): void {
    this.resetActivity();
    this.barcodesApi.findBarcodeProduct(barcode).subscribe(result => {
      if (!result.match) {
        this.audioFeedback.scanError();
        return;
      }
      const { product, barcode: barcodeData } = result.match;

      if (state.mode === 'info') {
        this.productsApi.findOne(product.id).subscribe(full => {
          const lastConsumed = full.recentActions.find(a => a.type === ProductActionType.Consume);
          const lastPurchased = full.recentActions.find(a => a.type === ProductActionType.Purchase);
          const scan: ScannerScanResult = {
            kind: 'info',
            productName: full.name,
            stockAmount: full.activeStockAmount,
            openedAmount: full.activeStockAmountOpened,
            unitName: full.quantityUnitStock?.namePlural ?? '',
            lastConsumedAt: lastConsumed ? new Date(lastConsumed.date) : null,
            lastPurchasedAt: lastPurchased ? new Date(lastPurchased.date) : null,
            timestamp: new Date(),
          };
          this.pushScan(scan);
        });
        return;
      }

      if (state.mode === 'consume-open') {
        const consumeAmount = product.quickConsumeAmount;
        const openAmount = barcodeData.amount ?? 1;
        const date = new Date().toISOString();
        const consumeBody = { amount: consumeAmount, date, spoiled: false };
        const openBody = { amount: openAmount, date };
        this.productsApi
          .executeProductAction(product.id, ProductActionType.Consume, consumeBody)
          .subscribe(afterConsume => {
            const consumeAction = afterConsume.recentActions[0];
            this.productsApi.executeProductAction(product.id, ProductActionType.Open, openBody).subscribe(afterOpen => {
              const scan: ScannerScanResult = {
                kind: 'consume-open',
                productName: afterOpen.name,
                consumeAmount,
                openAmount,
                unitName: afterOpen.quantityUnitStock?.namePlural ?? '',
                consumeStockItemId: consumeAction?.stockItem?.id ?? 0,
                timestamp: new Date(),
                undone: false,
              };
              this.pushScan(scan);
            });
          });
        return;
      }

      const actionType = state.mode === 'consume' ? ProductActionType.Consume : ProductActionType.Open;
      const amount = state.mode === 'consume' ? product.quickConsumeAmount : (barcodeData.amount ?? 1);
      const body =
        state.mode === 'consume'
          ? { amount, date: new Date().toISOString(), spoiled: false }
          : { amount, date: new Date().toISOString() };

      this.productsApi.executeProductAction(product.id, actionType, body).subscribe(updated => {
        const recentAction = updated.recentActions[0];
        const scan: ScannerScanResult = {
          kind: 'action',
          productName: updated.name,
          mode: state.mode as 'consume' | 'open',
          amount,
          unitName: updated.quantityUnitStock?.namePlural ?? '',
          stockItemId: recentAction?.stockItem?.id ?? 0,
          actionType,
          timestamp: new Date(),
          undone: false,
        };
        this.pushScan(scan);
      });
    });
  }

  private pushScan(scan: ScannerScanResult): void {
    const state = this.state$.value;
    if (state.status !== 'active') return;
    this.state$.next({
      ...state,
      lastScan: scan,
      recentScans: [scan, ...state.recentScans].slice(0, 10),
    });
    this.audioFeedback.scanSuccess();
  }
}
